/**
 * Lane-2 "context layers" — viewport-streamed regional overlays.
 *
 * Context layers render straight from the nationwide refdata snapshots
 * (`refdataBbox`) and are NEVER persisted: they are a field the user looks
 * at, not evidence. The 450-layer site cap, revision guard, and SED/report
 * read paths are untouched by however many features stream onto the map.
 * Promoting one feature to a saved site layer is the explicit bridge back
 * into the evidence lane — it flows through the SAME Esri→SiteLayer
 * converter as a regional fetch, so a promoted feature carries the exact
 * id/content-hash a dialog fetch of the same feature would have produced
 * (later fetches dedup instead of duplicating).
 */
import {
  ARCGIS_LAYER_CATALOG,
  type ArcgisCatalogEntry,
} from '../../config/arcgisLayerCatalog';
import {
  catalogEntryToConvertOptions,
  esriFeaturesToSiteLayers,
  refdataFeatureToEsriFeature,
} from '../../services/arcgis/esriJsonToSiteLayers';
import { colorForCategory } from '../../config/siteLayerColors';
import type { RefdataDatasetId, RefdataFeature } from '../../types/refdata';
import type { SiteLayer } from '../../types/site';

export interface ContextDatasetDescriptor {
  /** Refdata dataset id — the streaming key. */
  datasetId: RefdataDatasetId;
  /** Catalog entry backing labels, category, and promote conversion. */
  entry: ArcgisCatalogEntry;
  label: string;
  category: string;
  color: string;
}

/**
 * Every catalog entry backed by a refdata snapshot can stream as context.
 * Entries without a snapshot (live-only ArcGIS, PeeringDB) cannot — an
 * implicit per-viewport fetch must never touch live upstreams.
 */
export const CONTEXT_DATASETS: readonly ContextDatasetDescriptor[] =
  ARCGIS_LAYER_CATALOG
    .filter((entry): entry is ArcgisCatalogEntry & { refdataDataset: RefdataDatasetId } =>
      entry.refdataDataset != null)
    .map(entry => ({
      datasetId: entry.refdataDataset,
      entry,
      label: entry.label,
      category: entry.category,
      color: colorForCategory(entry.category),
    }));

export function contextDatasetById(datasetId: string): ContextDatasetDescriptor | undefined {
  return CONTEXT_DATASETS.find(d => d.datasetId === datasetId);
}

/** Human name for a streamed feature — same fields a saved layer would use. */
export function contextFeatureName(
  descriptor: ContextDatasetDescriptor,
  props: Record<string, string | number | null>,
): string {
  const { entry } = descriptor;
  const fields = entry.nameFields ?? (entry.nameField ? [entry.nameField] : []);
  const parts = fields
    .map(f => {
      const v = props[f];
      return v != null && String(v).trim().length > 0 ? String(v).trim() : undefined;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : entry.label;
}

/**
 * Promote one streamed feature into a saved SiteLayer. Returns [] when the
 * geometry is empty/degenerate. May return MULTIPLE layers for a multipart
 * feature (same part expansion as a regional fetch).
 */
export function contextFeatureToSiteLayers(
  descriptor: ContextDatasetDescriptor,
  feature: RefdataFeature,
): SiteLayer[] {
  const esri = refdataFeatureToEsriFeature(feature);
  if (!esri) return [];
  // Streamed features carry the snapshot's stable id in `feature.id`;
  // surface it as OBJECTID so the promoted layer's id matches what a
  // regional fetch of the same feature produces.
  esri.attributes = { OBJECTID: feature.id, ...esri.attributes };
  return esriFeaturesToSiteLayers([esri], catalogEntryToConvertOptions(descriptor.entry));
}
